/**
 * Lot Form Handlers
 *
 * Handles lot form submission for both local and remote modes,
 * including form validation, data normalization, and persistence
 *
 * @module eventHandlers/lotFormHandlers
 */

import type { State } from '../types';
import { closeLotModal } from '../modules/ui';
import { buildLotDraft, submitLotDraft } from '../modules/lotFormService';

export interface LotFormElements {
  form?: HTMLFormElement;
  f?: {
    id?: HTMLInputElement;
    title?: HTMLInputElement;
    source?: HTMLSelectElement;
    nextRequiredBid?: HTMLInputElement;
    close?: HTMLInputElement;
    url?: HTMLInputElement;
    description?: HTMLTextAreaElement;
  };
}

interface LotFormHandlerDeps {
  state: State;
  els: LotFormElements;
  render: () => void;
  applyModeBanner: () => void;
  DEBUG: boolean;
}

/**
 * Handle lot form submission
 */
async function handleLotFormSubmit(ev: Event, deps: LotFormHandlerDeps): Promise<void> {
  const { state, els, render, applyModeBanner, DEBUG } = deps;

  ev.preventDefault();

  if (!els.f) return;
  const now = Date.now();
  const existingLot = state.lots.find(
    (x: unknown) => (x as { id: string }).id === (els.f.id?.value || '')
  );
  const draft = buildLotDraft(els.f, existingLot, now);

  const result = await submitLotDraft(draft, { state, render, applyModeBanner, els, DEBUG });

  if (result.success && els.form) {
    els.form.reset();
  }
  if (result.success && els.f.id) {
    els.f.id.value = '';
  }

  if (result.success) {
    render();
    closeLotModal({ reset: true });
  }
}

/**
 * Setup lot form submission handler
 */
export function setupLotFormHandlers(deps: LotFormHandlerDeps): void {
  const { els } = deps;

  if (!els.form) return;

  els.form.addEventListener('submit', async (ev: Event) => {
    await handleLotFormSubmit(ev, deps);
  });
}
